import { useState } from "react";
import { Heart } from "lucide-react";
import { HttpError, handleApiError } from "@/lib/errors";
import { trackFastgiverStarted } from "@/lib/analytics";

const PRESET_AMOUNTS = [50, 100, 200, 500];

const FastgiverForm = () => {
  const [amount, setAmount] = useState<number>(100);
  const [customAmount, setCustomAmount] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState("");

  const handlePreset = (value: number) => {
    setAmount(value);
    setIsCustom(false);
    setCustomAmount("");
    setValidationError("");
  };

  const handleCustom = (value: string) => {
    setCustomAmount(value);
    setIsCustom(true);
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed) && parsed >= 1) {
      setAmount(parsed);
      setValidationError("");
    }
  };

  const handleSubmit = async () => {
    if (loading) return;

    const finalAmount = isCustom ? parseInt(customAmount, 10) : amount;
    if (!finalAmount || finalAmount < 1) {
      setValidationError("Velg et beløp på minst 1 kr");
      return;
    }

    setLoading(true);
    setValidationError("");
    trackFastgiverStarted({ amountNok: finalAmount });

    try {
      const res = await fetch("/api/vipps-recurring?action=create-agreement", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: finalAmount,
          phoneNumber: phoneNumber || undefined,
          email: email || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new HttpError(res.status, data?.error ?? "Vipps-forespørsel feilet");
      }

      const data = await res.json();

      if (!data.vippsConfirmationUrl) {
        throw new Error("Mottok ikke Vipps-lenke");
      }

      window.location.href = data.vippsConfirmationUrl;
    } catch (err) {
      handleApiError(err, {
        source: "FastgiverForm.submit",
        fallbackMessage: "Kunne ikke opprette fast giver-avtale. Prøv igjen.",
        context: { amount: finalAmount },
      });
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-background p-8 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <Heart className="h-6 w-6 text-primary" />
        <h3 className="text-xl font-bold font-heading">Bli fast giver</h3>
      </div>

      <p className="text-sm text-muted-foreground font-body mb-6">
        Velg et månedlig beløp. Du kan endre eller avslutte avtalen når som
        helst i Vipps-appen.
      </p>

      <div className="grid grid-cols-2 gap-3 mb-4">
        {PRESET_AMOUNTS.map((value) => (
          <button
            key={value}
            onClick={() => handlePreset(value)}
            className={`rounded-xl px-4 py-3 text-sm font-semibold font-body transition-all duration-200 ${
              !isCustom && amount === value
                ? "bg-primary text-primary-foreground shadow-md"
                : "border border-border text-muted-foreground hover:border-primary hover:text-primary"
            }`}
          >
            {value} kr/mnd
          </button>
        ))}
      </div>

      <div className="mb-4">
        <input
          type="number"
          min="1"
          aria-label="Annet beløp i kroner per måned"
          placeholder="Annet beløp (kr/mnd)"
          value={customAmount}
          onChange={(e) => handleCustom(e.target.value)}
          className={`w-full rounded-xl border px-4 py-3 text-sm font-body outline-none transition-colors ${
            isCustom
              ? "border-primary ring-1 ring-primary/20"
              : "border-border"
          } focus:border-primary focus:ring-1 focus:ring-primary/20`}
        />
      </div>

      <div className="mb-4">
        <input
          type="tel"
          aria-label="Mobilnummer (valgfritt)"
          placeholder="Mobilnummer (valgfritt)"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          className="w-full rounded-xl border border-border px-4 py-3 text-sm font-body outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary/20"
        />
      </div>

      <div className="mb-2">
        <input
          type="email"
          aria-label="E-post for kvittering (valgfritt)"
          placeholder="E-post (valgfritt, for kvittering)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-xl border border-border px-4 py-3 text-sm font-body outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary/20"
        />
      </div>
      <p className="mb-6 text-xs text-muted-foreground font-body">
        Oppgir du e-post sender vi deg velkomst, månedlig kvittering og varsler om trekk.
      </p>

      {validationError && (
        <p className="text-sm text-red-500 font-body mb-4">{validationError}</p>
      )}

      <button
        onClick={handleSubmit}
        disabled={loading}
        className="w-full rounded-xl bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground font-body shadow-lg transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? "Kobler til Vipps..." : `Gi ${isCustom ? customAmount || "..." : amount} kr/mnd med Vipps`}
      </button>

      <p className="mt-4 text-xs text-center text-muted-foreground font-body">
        Ingen bindingstid. Les{" "}
        <a href="/vilkar-fastgiver" className="underline text-primary">
          vilkårene
        </a>
        .
      </p>
    </div>
  );
};

export default FastgiverForm;
